import { useEffect, useRef, useState } from 'react';
import { useHeaderSearchStore, type HeaderSearchBinding } from './header-search-store';

/**
 * Binds the app-header search field to this page for as long as it is mounted.
 * `value` is the page's committed search (URL param); `onChange` receives the
 * debounced, trimmed text ('' = cleared). Pass `enabled: false` to leave the
 * header field unbound (e.g. a variant of the page that has no search).
 */
export function useHeaderSearch({
  value,
  placeholder,
  onChange,
  enabled = true,
}: HeaderSearchBinding & { enabled?: boolean }) {
  const [owner] = useState(() => Symbol('header-search'));
  // Keep the registered callback stable while always calling the latest one.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (!enabled) return;
    useHeaderSearchStore.getState().register(owner, {
      value,
      placeholder,
      onChange: (next) => onChangeRef.current(next),
    });
  }, [owner, enabled, value, placeholder]);

  // Separate from the register effect so a value change never unbinds (which
  // would blank the header draft); only unmount / disable releases.
  useEffect(
    () => () => {
      useHeaderSearchStore.getState().release(owner);
    },
    [owner, enabled],
  );
}
